import {
  skyFlightFrame,
  skyFlightPointToLocal,
  skyFlightPointToWorld,
} from '../../../packages/domain/src/92-sky-flight-frame.mjs';

const finite = (point) => point && ['x', 'y', 'z'].every((key) => Number.isFinite(point[key]));
/** Metres ahead of the physical observer eye. The two-centimetre plane keeps
 * the rendered peripheral neck and equipment intact through live close runs;
 * it does not move the eye, hide additional geometry or change collision. */
export const SKY_OBSERVER_NEAR = 0.02;
const clamp = (value) => Math.max(0, Math.min(1, value));
const mix = (a, b, t) =>
  Object.fromEntries(['x', 'y', 'z'].map((key) => [key, a[key] + (b[key] - a[key]) * t]));
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const subtract = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const cross = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
function unit(point) {
  const length = Math.hypot(point.x, point.y, point.z);
  return length > 1e-8 ? { x: point.x / length, y: point.y / length, z: point.z / length } : null;
}
function observerFov(observer, target, up, aspect) {
  const forward = unit(subtract(target, observer.eye));
  const right = forward && unit(cross(forward, up));
  if (!right) return 76;
  const vertical = cross(right, forward);
  let tangent = Math.tan((76 * Math.PI) / 360);
  for (const barrel of observer.barrels ?? []) {
    if (!finite(barrel)) continue;
    const offset = subtract(barrel, observer.eye),
      depth = dot(offset, forward);
    if (depth <= 0.12) continue;
    tangent = Math.max(
      tangent,
      Math.abs(dot(offset, right)) / (depth * aspect * 0.9),
      Math.abs(dot(offset, vertical)) / (depth * 0.9),
    );
  }
  return Math.min(94, (Math.atan(tangent) * 360) / Math.PI);
}

/** Local-observer geometry only. Head/helmet triangles follow actual head-bone
 * skin weights; the shared imported geometry and all materials stay untouched.
 * The private masked index is allocated lazily, restored on exit and disposed
 * once. Other pilot instances retain their complete geometry. Eye offset is in
 * the initial pilot's metre axes, captured into the real head's local space. */
export function createSkyObserverHead(T, model, head) {
  const records = [];
  const eye = new T.Vector3(),
    headPoint = new T.Vector3();
  let masked = false,
    disposed = false;
  if (head) {
    head.updateWorldMatrix(true, false);
    head.getWorldPosition(headPoint);
    eye.copy(headPoint).add(new T.Vector3(0, 0.06, -0.13));
    head.worldToLocal(eye);
    model.traverse((mesh) => {
      if (!mesh.isSkinnedMesh) return;
      const geometry = mesh.geometry,
        indices = geometry.attributes.skinIndex,
        weights = geometry.attributes.skinWeight;
      if (!indices || !weights) return;
      const headBones = new Set();
      mesh.skeleton.bones.forEach((bone, index) => {
        for (let parent = bone; parent; parent = parent.parent)
          if (parent === head) {
            headBones.add(index);
            break;
          }
      });
      const selected = Array.from({ length: geometry.attributes.position.count }, (_, index) => {
        let influence = 0;
        for (let slot = 0; slot < 4; slot++)
          if (headBones.has(indices.getComponent(index, slot)))
            influence += weights.getComponent(index, slot);
        return influence > 0.35;
      });
      if (selected.some(Boolean))
        records.push({ mesh, original: geometry, selected, masked: null });
    });
  }
  function setMask(enabled) {
    if (disposed) return;
    masked = Boolean(enabled) && records.length > 0;
    for (const record of records) {
      if (masked && !record.masked) {
        const original = record.original;
        const count = original.index?.count ?? original.attributes.position.count;
        const at = (index) => (original.index ? original.index.getX(index) : index);
        const groups = original.groups.length
          ? original.groups
          : [{ start: 0, count, materialIndex: 0 }];
        const kept = [],
          geometry = original.clone();
        geometry.clearGroups();
        for (const group of groups) {
          const start = kept.length;
          for (
            let index = group.start;
            index < Math.min(count, group.start + group.count);
            index += 3
          ) {
            const triangle = [at(index), at(index + 1), at(index + 2)];
            if (!triangle.some((vertex) => record.selected[vertex])) kept.push(...triangle);
          }
          geometry.addGroup(start, kept.length - start, group.materialIndex);
        }
        geometry.setIndex(kept);
        record.masked = geometry;
      }
      record.mesh.geometry = masked ? record.masked : record.original;
    }
  }
  return {
    setMask,
    eye() {
      if (disposed || !head) return null;
      head.updateWorldMatrix(true, false);
      const position = head.localToWorld(eye.clone());
      head.getWorldPosition(headPoint);
      return {
        eye: { x: position.x, y: position.y, z: position.z },
        head: { x: headPoint.x, y: headPoint.y, z: headPoint.z },
        masked,
      };
    },
    dispose() {
      if (disposed) return;
      setMask(false);
      disposed = true;
      for (const record of records) record.masked?.dispose();
    },
  };
}

/** Render-only first-person option. All coordinates are world metres. Caller
 * supplies the accepted 4m history camera and physical pilot/basis/eye/aim; this
 * function never changes actors, contacts or control. Blend is allowed only on
 * a caller-approved straight portal segment. Optional isClear(frame) checks
 * actual scene/frustum geometry; a blocked proposal retains a clear history
 * camera, or returns null if neither is safe. Reduced motion selects endpoints.
 * Local head masking is binary geometry removal, never world transparency.
 * The endpoint uses the canonical2cm near plane: peripheral animated neck
 * triangles cross a4cm plane during close steering. The final15% of the blend
 * already uses2cm. A bounded76–94degree lens includes physical
 * barrel tips with a 10% screen margin; caller retains temporal lens smoothing.
 * Invalid coordinates, basis, lens or aspect return null. */
export function skyInteriorCameraFrame({
  flightFrame,
  pilot,
  observer,
  thirdPerson,
  aim,
  aspect = 390 / 844,
  progress = 0,
  blendAllowed = false,
  reduced = false,
  isClear = () => true,
} = {}) {
  const basis = skyFlightFrame(flightFrame);
  if (
    !basis ||
    !finite(pilot) ||
    !finite(observer?.eye) ||
    !finite(observer?.head) ||
    !finite(thirdPerson) ||
    !Number.isFinite(progress) ||
    !Number.isFinite(aspect) ||
    aspect <= 0
  )
    return null;
  if (
    typeof isClear !== 'function' ||
    (thirdPerson.fov != null &&
      (!Number.isFinite(thirdPerson.fov) || thirdPerson.fov <= 0 || thirdPerson.fov >= 180)) ||
    (thirdPerson.near != null && (!Number.isFinite(thirdPerson.near) || thirdPerson.near <= 0))
  )
    return null;
  let t = clamp(progress);
  if (reduced) t = t >= 0.5 ? 1 : 0;
  if (t > 0 && t < 1 && !blendAllowed) t = 0;
  const start = { x: thirdPerson.x, y: thirdPerson.y, z: thirdPerson.z };
  const relative = { ...basis, origin: pilot };
  const control1 = skyFlightPointToWorld(relative, { x: 0.95, y: 1.25, z: 1.8 });
  const control2 = skyFlightPointToWorld(relative, { x: 0.9, y: 1.25, z: -0.9 });
  const end = observer.eye;
  const point = Object.fromEntries(
    ['x', 'y', 'z'].map((key) => [
      key,
      (1 - t) ** 3 * start[key] +
        3 * (1 - t) ** 2 * t * control1[key] +
        3 * (1 - t) * t ** 2 * control2[key] +
        t ** 3 * end[key],
    ]),
  );
  const eyeBasis = { ...basis, origin: end };
  const target =
    finite(aim) && skyFlightPointToLocal(eyeBasis, aim).z < -0.05
      ? aim
      : skyFlightPointToWorld(eyeBasis, { x: 0, y: 0, z: -10 });
  const fromLook = { x: thirdPerson.lookX, y: thirdPerson.lookY, z: thirdPerson.lookZ };
  if (!finite(fromLook)) return null;
  const look = mix(fromLook, target, t);
  const nearWeight = Math.min(1, t / 0.85);
  const near = (thirdPerson.near ?? 0.15) * (1 - nearWeight) + SKY_OBSERVER_NEAR * nearWeight;
  const frame = {
    ...point,
    lookX: look.x,
    lookY: look.y,
    lookZ: look.z,
    up: { ...basis.up },
    fov: (thirdPerson.fov ?? 54) * (1 - t) + observerFov(observer, target, basis.up, aspect) * t,
    near,
    maskWeight:
      t > 0.5 &&
      Math.hypot(point.x - observer.head.x, point.y - observer.head.y, point.z - observer.head.z) <
        0.6
        ? 1
        : 0,
    firstPerson: t === 1,
    blocked: false,
  };
  if (isClear(frame)) return frame;
  const fallback = {
    ...thirdPerson,
    up: thirdPerson.up ?? { ...basis.up },
    maskWeight: 0,
    firstPerson: false,
    blocked: true,
  };
  return isClear(fallback) ? fallback : null;
}
